import {
  Alert,
  Box,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { PropsWithChildren, SubmitEventHandler } from 'react';

import { Icon } from '@/components/Icon/Icon';
import { Divider } from '@/components/Layout/Divider';
import { FullPageLoaderOverlay } from '@/components/Layout/FullPageLoader';
import { QuestionProps } from '@/components/PageForm/MoreInfoForm/drawers/QuestionsDrawer/QuestionDrawer.interface';

import { useTranslate } from '@/hooks/useTranslate';

import { getAPIErrorMessage } from '@/utils/api';
import { getDataTestId } from '@/utils/browser';
import { isRichText, renderHTMLText } from '@/utils/string';

import {
  HintIconWrapper,
  ProtectedImg,
  QuestionRichText,
  SeeMoreDetailsButton,
  TooltipRichText,
  ToolTipWrapper,
} from './QuestionDrawer.styled';

type QuestionFormProps = {
  answerError?: Error | null;
  isLoading?: boolean;
  hideAdditionalInfo?: boolean;
  onSubmit?: SubmitEventHandler<HTMLFormElement>;
} & Omit<QuestionProps, 'onAnswered'> &
  PropsWithChildren;

export function QuestionForm({
  index,
  total,
  question,
  image,
  answerError,
  isLoading,
  hideAdditionalInfo,
  onSubmit,
  children,
}: QuestionFormProps) {
  const { t } = useTranslate();
  const theme = useTheme();
  const richAdditionalInfo = isRichText(question.additionalInfo)
    ? question.additionalInfo
    : undefined;
  return (
    <>
      <Typography variant="h3" textAlign="center" mt={4} mb={1}>
        {t('questionDrawer.questionForm.title')}
      </Typography>
      <Typography
        variant="body2"
        color={theme.palette.neutral[800]}
        textAlign="center"
      >
        {t('questionDrawer.questionForm.body')}
      </Typography>
      {image || <ProtectedImg />}
      {answerError && (
        <Alert icon={<Icon name="alert" />} color="error" variant="standard">
          {getAPIErrorMessage(answerError)}
        </Alert>
      )}
      <Box alignSelf="stretch" component="form" onSubmit={onSubmit}>
        <Typography
          variant="body1"
          color={theme.palette.text.secondary}
          textAlign="center"
          mb={1}
        >
          {t('questionDrawer.questionForm.formBody', { no: index + 1, total })}
        </Typography>
        <Typography
          variant="h4"
          textAlign="center"
          mb={question.additionalInfo ? 2 : 4}
        >
          {question.question}{' '}
          {!richAdditionalInfo && question.info && (
            <Tooltip
              title={question.info}
              placement="top"
              arrow
              enterTouchDelay={0}
            >
              <HintIconWrapper {...getDataTestId('question-hint-icon')}>
                <Icon
                  name="info-outline"
                  color={theme => theme.palette.neutral[700]}
                />
              </HintIconWrapper>
            </Tooltip>
          )}
        </Typography>
        {richAdditionalInfo && !hideAdditionalInfo && (
          <Stack direction="column" mb={4} gap={2}>
            <Divider />
            <QuestionRichText {...getDataTestId('question-additional-info')}>
              {renderHTMLText(richAdditionalInfo)}
            </QuestionRichText>
            {question.info && (
              <Tooltip
                title={
                  <TooltipRichText>
                    {renderHTMLText(question.info)}
                  </TooltipRichText>
                }
                placement="top"
                arrow
                enterTouchDelay={0}
              >
                <SeeMoreDetailsButton
                  type="button"
                  {...getDataTestId('question-see-more-details')}
                >
                  <Icon name="info-outline" />
                  {t('questionDrawer.questionForm.seeMoreDetails')}
                </SeeMoreDetailsButton>
              </Tooltip>
            )}
          </Stack>
        )}
        {!richAdditionalInfo && question.additionalInfo && (
          <Typography
            variant="caption"
            color={theme.palette.text.secondary}
            textAlign="center"
            mb={4}
          >
            {question.additionalInfo}
          </Typography>
        )}
        {question.description && (
          <Typography variant="body2" textAlign="center" mt={1}>
            {question.description}{' '}
            {question.tooltip && (
              <Tooltip
                title={question.tooltip}
                placement="top"
                arrow
                enterTouchDelay={0}
              >
                <ToolTipWrapper>
                  <Icon
                    name="info-outline"
                    color={theme => theme.palette.neutral[700]}
                    sx={{ verticalAlign: 'middle' }}
                  />
                </ToolTipWrapper>
              </Tooltip>
            )}
          </Typography>
        )}
        <Stack direction="column" mt={4} gap={4} alignItems="stretch">
          {children}
        </Stack>
      </Box>
      {isLoading && <FullPageLoaderOverlay />}
    </>
  );
}
